import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useColleges, searchColleges } from '../utils/colleges';
import { MIN_SCHOOL_QUERY_CHARS, MAX_SCHOOL_RESULTS } from '../hooks/useSchoolData';
import CollegeLogo from '../components/CollegeLogo';
import MatchDetailModal from '../components/MatchDetailModal';
import { PageHeader, MonoLabel, EmptyState } from '../components/Editorial';

// Same minimum and ceiling as every other school/college box.
const MIN_QUERY_LEN = MIN_SCHOOL_QUERY_CHARS;
const DEBOUNCE_MS = 200;

// Human meta line for a result row: "division · city, state" — omit whichever
// pieces are missing (most non-athletics schools carry no division).
function schoolMeta(c) {
  const parts = [];
  if (c.d) parts.push(c.d);
  const loc = [c.c, c.s].filter(Boolean).join(', ');
  if (loc) parts.push(loc);
  return parts.join(' · ');
}

export default function SchoolSearchPage() {
  const { API_URL } = useAuth();
  const navigate = useNavigate();
  const colleges = useColleges();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [matches, setMatches] = useState([]);
  const [selected, setSelected] = useState(null);

  // One-time matches fetch — gives us the score ring + full match payload
  // (score, reasons, logo_url) for any school in the athlete's match set,
  // same call MatchesPanel uses.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/athletes/matches?limit=60`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!cancelled) setMatches(res.data?.matches || []);
      } catch (err) {
        console.error('Error fetching matches for school search:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [API_URL]);

  // Debounce the query before filtering the (up to ~6,100-row) college list.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const matchByUnitid = useMemo(() => {
    const m = {};
    matches.forEach((mm) => { m[mm.unitid] = mm; });
    return m;
  }, [matches]);

  const trimmedQuery = debouncedQuery.trim();

  // The shared ranked matcher (the same one every college box uses): every typed
  // word must start a word of the name, city or state, "St"/"St."/"Saint" are one
  // word, and a name that starts with the query outranks one that only contains it.
  // This page used a raw substring on the name, so "st johns" never found
  // "St. John's" and a city or state could not be searched.
  const found = useMemo(() => (
    colleges && trimmedQuery.length >= MIN_QUERY_LEN
      ? searchColleges(colleges, trimmedQuery, MAX_SCHOOL_RESULTS + 1)
      : []
  ), [colleges, trimmedQuery]);
  const results = found.slice(0, MAX_SCHOOL_RESULTS);
  const hasMore = found.length > MAX_SCHOOL_RESULTS;

  const openSchool = (c) => {
    const match = matchByUnitid[c.u];
    setSelected(match ? { ...match } : { unitid: c.u, name: c.n, city: c.c, state: c.s, division: c.d });
  };

  return (
    <div className="ed-page">
      <PageHeader
        eyebrow={<MonoLabel>RECRUITING · SEARCH SCHOOLS</MonoLabel>}
        title="Look up any school"
        sub="Type a name to investigate a program on your own — whether or not it's one of your matches."
      />

      <input
        type="text"
        className="search-bar"
        placeholder="Search schools by name..."
        aria-label="Search schools by name"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoComplete="off"
      />

      {trimmedQuery.length >= MIN_QUERY_LEN && results.length === 0 && (
        <EmptyState
          title={`No schools match "${trimmedQuery}"`}
          body="Check the spelling, or try the school's full name."
        />
      )}

      {results.length > 0 && (
        <div className="ed-list">
          {results.map((c) => {
            const match = matchByUnitid[c.u];
            const meta = schoolMeta(c);
            return (
              <div
                key={c.u}
                className="school-search__row"
                role="button"
                tabIndex={0}
                aria-label={`View details for ${c.n}`}
                onClick={() => openSchool(c)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openSchool(c);
                  }
                }}
              >
                <CollegeLogo schoolName={c.n} unitid={c.u} size={44} />
                <div className="school-search__id">
                  <div className="school-search__name">{c.n}</div>
                  {meta && <div className="school-search__meta">{meta}</div>}
                </div>
                {match && (
                  <div className="school-search__score" aria-label={`Match score ${match.score}`}>
                    {match.score}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {hasMore && (
        <p className="school-search__more" role="note">
          Showing the first {MAX_SCHOOL_RESULTS} matches — keep typing (name, city or state) to narrow.
        </p>
      )}

      <div className="school-search__footer">
        <button type="button" className="school-search__footerlink" onClick={() => navigate('/recruit?mode=schools&tab=matches')}>
          Or see the schools that fit you →
        </button>
      </div>

      {selected && <MatchDetailModal match={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
